import {
  API_KEY_PRESET_SCOPES,
  API_KEY_SCOPE_CODES,
  type ApiKeyPreset,
  type ApiKeyScope,
  type CreateApiKeyRequest,
} from "@webyonetim/contracts";
import * as Dialog from "radix-ui/dialog";
import { useEffect, useMemo, useState, type SyntheticEvent } from "react";

import { Button, Checkbox, SelectField, TextField } from "../../components/ui/Controls";
import { AlertBanner } from "../../components/ui/Feedback";
import { OneTimeSecret } from "../../components/ui/SecurityDialogs";
import { apiErrorMessage } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { apiKeysApi } from "../../lib/api";

const presetLabels: Readonly<Record<ApiKeyPreset, string>> = {
  observe: "Gözlemle",
  deploy: "Dağıt",
  operate: "İşlet",
  custom: "Özel",
};
const scopeLabels: Readonly<Record<ApiKeyScope, string>> = {
  "system:read": "Sistem durumunu görüntüle",
  "audit:read": "Denetim kaydını görüntüle",
  "projects:read": "Projeleri görüntüle",
  "hosts:read": "Sunucuları görüntüle",
  "deployments:read": "Dağıtımları görüntüle",
  "deployments:create": "Dağıtım başlat",
  "deployments:cancel": "Dağıtımı iptal et",
  "deployments:rollback": "Rollback başlat",
  "jobs:read": "İşleri görüntüle",
  "jobs:cancel": "İşi iptal et",
  "logs:read": "Logları görüntüle",
  "config:read": "Yapılandırmayı görüntüle",
  "config:write": "Yapılandırmayı değiştir",
  "secrets:write": "Gizli değer yaz",
  "domains:read": "Domainleri görüntüle",
  "domains:write": "Domainleri değiştir",
  "databases:read": "Veritabanlarını görüntüle",
  "databases:operate": "Veritabanını işlet",
  "databases:backup": "Yedek oluştur",
  "databases:restore": "Yedek geri yükle",
};

function expiryFromDays(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
}

export function ApiKeyCreateDialog({
  onCreated,
  onOpenChange,
  open,
}: {
  readonly onCreated: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}) {
  const [step, setStep] = useState<"form" | "review">("form");
  const [label, setLabel] = useState("");
  const [preset, setPreset] = useState<ApiKeyPreset>("observe");
  const [customScopes, setCustomScopes] = useState<readonly ApiKeyScope[]>(["system:read"]);
  const [expiryChoice, setExpiryChoice] = useState("30");
  const [customExpiry, setCustomExpiry] = useState("");
  const [allResources, setAllResources] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [secret, setSecret] = useState<string>();
  useEffect(() => {
    if (!open) {
      setStep("form");
      setLabel("");
      setPreset("observe");
      setCustomScopes(["system:read"]);
      setExpiryChoice("30");
      setCustomExpiry("");
      setAllResources(false);
      setError(undefined);
      setSecret(undefined);
    }
  }, [open]);
  const scopes = useMemo<readonly ApiKeyScope[]>(
    () => (preset === "custom" ? customScopes : API_KEY_PRESET_SCOPES[preset]),
    [customScopes, preset],
  );
  const customExpiryDate = customExpiry === "" ? undefined : new Date(customExpiry);
  const customExpiryValid =
    customExpiryDate !== undefined && !Number.isNaN(customExpiryDate.getTime());
  const expiresAt =
    expiryChoice === "custom"
      ? customExpiryValid
        ? customExpiryDate.toISOString()
        : ""
      : expiryFromDays(Number(expiryChoice));
  const request = useMemo<CreateApiKeyRequest>(
    () => ({
      label,
      preset,
      scopes: [...scopes],
      resourceBoundary: allResources
        ? { mode: "all_resources" }
        : { mode: "restricted", projectIds: [], hostIds: [] },
      expiresAt,
    }),
    [allResources, expiresAt, label, preset, scopes],
  );
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step === "form") {
      setStep("review");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const response = await apiKeysApi.create(request);
      setSecret(response.secret);
      onCreated();
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  if (secret !== undefined)
    return (
      <OneTimeSecret
        acknowledgement="Anahtarı güvenli bir yere kaydettim"
        body="Bu anahtar yalnızca şimdi gösterilecek. Güvenli bir parola kasasına kaydedin; kapattıktan sonra yeniden görüntüleyemezsiniz."
        closeLabel="API anahtarı penceresini kapat"
        copyLabel="Anahtarı kopyala"
        heading="API anahtarınız hazır"
        onClose={() => {
          setSecret(undefined);
          onOpenChange(false);
        }}
        secret={secret}
      />
    );
  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title>API anahtarı oluştur</Dialog.Title>
          <Dialog.Description>
            Otomasyona yalnızca gerekli kapsamları, kaynak sınırını ve süreyi verin.
          </Dialog.Description>
          <form
            className="form-stack"
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            {step === "form" ? (
              <>
                <TextField
                  label="Anahtar etiketi"
                  maxLength={120}
                  onChange={(event) => {
                    setLabel(event.currentTarget.value);
                  }}
                  required
                  value={label}
                />
                <SelectField
                  label="İzin hazır ayarı"
                  onChange={(event) => {
                    setPreset(event.currentTarget.value as ApiKeyPreset);
                  }}
                  value={preset}
                >
                  {Object.entries(presetLabels).map(([code, text]) => (
                    <option key={code} value={code}>
                      {text}
                    </option>
                  ))}
                </SelectField>
                {preset === "custom" ? (
                  <fieldset className="card">
                    <legend>Etkin kapsamlar</legend>
                    {API_KEY_SCOPE_CODES.map((scope) => (
                      <Checkbox
                        checked={customScopes.includes(scope)}
                        key={scope}
                        label={
                          <>
                            <code>{scope}</code> — {scopeLabels[scope]}
                          </>
                        }
                        onChange={(event) => {
                          setCustomScopes((current) =>
                            event.currentTarget.checked
                              ? [...current, scope]
                              : current.filter((item) => item !== scope),
                          );
                        }}
                      />
                    ))}
                  </fieldset>
                ) : null}
                <fieldset className="card">
                  <legend>Kaynak sınırı</legend>
                  <p>
                    Bu aşamada proje ve sunucu seçimi bulunmadığından anahtar boş bir kısıtlı
                    sınırla oluşturulur.
                  </p>
                  <Checkbox
                    checked={allResources}
                    label="Tüm mevcut ve gelecekteki kaynaklar"
                    onChange={(event) => {
                      setAllResources(event.currentTarget.checked);
                    }}
                  />
                  {allResources ? (
                    <AlertBanner heading="Geniş kaynak erişimi" tone="warning">
                      <p>
                        Bu seçim gelecekte eklenen kaynakları da kapsar. Yalnızca gerekli olduğunda
                        kullanın.
                      </p>
                    </AlertBanner>
                  ) : null}
                </fieldset>
                <SelectField
                  label="Son kullanma"
                  onChange={(event) => {
                    setExpiryChoice(event.currentTarget.value);
                  }}
                  value={expiryChoice}
                >
                  <option value="7">7 gün</option>
                  <option value="30">30 gün</option>
                  <option value="90">90 gün</option>
                  <option value="custom">Özel tarih</option>
                </SelectField>
                {expiryChoice === "custom" ? (
                  <TextField
                    label="Özel son kullanma tarihi"
                    min={new Date().toISOString().slice(0, 16)}
                    onChange={(event) => {
                      setCustomExpiry(event.currentTarget.value);
                    }}
                    required
                    type="datetime-local"
                    value={customExpiry}
                  />
                ) : null}
              </>
            ) : (
              <section className="card stack">
                <h2>Oluşturma öncesi inceleme</h2>
                <dl>
                  <dt>Etiket</dt>
                  <dd>{label}</dd>
                  <dt>İzin hazır ayarı</dt>
                  <dd>{presetLabels[preset]}</dd>
                  <dt>Kaynak sınırı</dt>
                  <dd>
                    {allResources
                      ? "Tüm mevcut ve gelecekteki kaynaklar"
                      : "Kısıtlı: 0 proje, 0 sunucu"}
                  </dd>
                  <dt>Son kullanma</dt>
                  <dd>{formatDateTime(expiresAt)}</dd>
                </dl>
                <h3>Etkin kapsamlar</h3>
                <ul>
                  {scopes.map((scope) => (
                    <li key={scope}>
                      <code>{scope}</code> — {scopeLabels[scope]}
                    </li>
                  ))}
                </ul>
                <h3>Bu anahtarın yapamayacakları</h3>
                <p>
                  Ekip üyelerini, API anahtarlarını veya hesap güvenliği ayarlarını yönetemez; gizli
                  değerleri okuyamaz.
                </p>
              </section>
            )}
            {error === undefined ? null : (
              <AlertBanner heading="API anahtarı oluşturulamadı" tone="error">
                <p>{error}</p>
              </AlertBanner>
            )}
            <div className="form-actions">
              {step === "review" ? (
                <Button
                  onClick={() => {
                    setStep("form");
                  }}
                  variant="secondary"
                >
                  Bilgileri değiştir
                </Button>
              ) : null}
              <Button
                busy={busy}
                busyLabel="Anahtar oluşturuluyor…"
                disabled={scopes.length === 0 || (expiryChoice === "custom" && !customExpiryValid)}
                type="submit"
              >
                {step === "form" ? "İncelemeye geç" : "Anahtarı oluştur"}
              </Button>
              <Dialog.Close asChild>
                <Button variant="secondary">Oluşturmayı iptal et</Button>
              </Dialog.Close>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export { scopeLabels };
